import { Classes, Dialog } from '@blueprintjs/core';
import React, { useCallback, useMemo, useState } from 'react';
import { TLCode } from '../../common/enums';
import { TranslatedMessage } from '../common/translation';
import { Button } from 'antd';
import { getFirstMonday, getFirstSunday } from '../../common/utils';
import { Instance } from 'mobx-state-tree';
import { DutyAssignment, Pharmacy } from '../../store/models';
import { DivLine } from '../common/styledComponents/StyledComponents';
import DateInput from '../common/input/DateInput';
import moment from 'moment';

interface TransferDutiesDialogProps {
  fromPharmacy?: Instance<typeof Pharmacy>;
  toPharmacy?: Instance<typeof Pharmacy>;
  isOpen: boolean;
  transferDutiesToPharmacy: (duties: Array<Instance<typeof DutyAssignment>>) => void;
  onCancel: () => void;
  allDuties: Array<Instance<typeof DutyAssignment>>;
  toPharmacyBusyDates: Array<Date>;
}

export const TransferDutiesDialog = ({
  fromPharmacy,
  toPharmacy,
  isOpen,
  onCancel,
  transferDutiesToPharmacy,
  allDuties,
  toPharmacyBusyDates,
}: TransferDutiesDialogProps) => {
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());

  const dutiesToTransfer = useMemo(() => {
    return allDuties
      .filter((da) => {
        if (da.duty === undefined) return false;
        const date = new Date(da.duty.dutyDate.date);
        return date >= fromDate && date <= toDate;
      })
      .sort((a, b) => {
        const aDate = new Date(a.duty!.dutyDate.date);
        const bDate = new Date(b.duty!.dutyDate.date);
        return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
      });
  }, [allDuties, fromDate, toDate]);

  const transferDuties = useCallback(() => {
    transferDutiesToPharmacy(dutiesToTransfer);
  }, [transferDutiesToPharmacy, dutiesToTransfer]);

  const setS1Date = useCallback(() => {
    const firstMonday = getFirstMonday(fromDate.getFullYear(), 0);
    const firstSunday = getFirstSunday(fromDate.getFullYear(), 6);
    setFromDate(firstMonday);
    setToDate(firstSunday);
  }, [fromDate, setFromDate, setToDate]);

  const setS2Date = useCallback(() => {
    const firstMonday = getFirstMonday(fromDate.getFullYear(), 6);
    const firstSunday = getFirstSunday(fromDate.getFullYear() + 1, 0);
    setFromDate(firstMonday);
    setToDate(firstSunday);
  }, [fromDate, setFromDate, setToDate]);

  const isBusyDateForTarget = useMemo(
    () => (date: Date) => {
      return (
        toPharmacyBusyDates.find(
          (d) =>
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear(),
        ) !== undefined
      );
    },
    [toPharmacyBusyDates],
  );

  return (
    <Dialog isOpen={isOpen}
      title={<TranslatedMessage id={TLCode.DUTIES_TransferDialogTitle} />}
      canOutsideClickClose={false}>
      <div className={Classes.DIALOG_BODY}>
        <DivLine>
          <TranslatedMessage id={TLCode.DUTIES_TransferDialog_From} />
          {' : ' + (fromPharmacy === undefined ? '' : fromPharmacy!.name)}
        </DivLine>
        <DivLine>
          <TranslatedMessage id={TLCode.DUTIES_TransferDialog_To} />
          {' : ' + (toPharmacy === undefined ? '' : toPharmacy!.name)}
        </DivLine>
        <DivLine>
          <TranslatedMessage id={TLCode.DUTIES_TransferDialog_FromDate} />
          <DateInput
            value={fromDate}
            canClearSelection={false}
            onChange={(value) => {
              setFromDate(value);
            }}
          />
          <Button onClick={setS1Date}>
            <TranslatedMessage id={TLCode.ROSTER_S1} />
          </Button>
          <Button onClick={setS2Date}>
            <TranslatedMessage id={TLCode.ROSTER_S2} />
          </Button>
        </DivLine>
        <DivLine>
          <TranslatedMessage id={TLCode.DUTIES_TransferDialog_ToDate} />
          <DateInput
            value={toDate}
            canClearSelection={false}
            onChange={(value) => {
              setToDate(value);
            }}
          />
        </DivLine>
        <DivLine>
          <TranslatedMessage id={TLCode.DUTIES_TransferDialog_DatesToTransfer} />
        </DivLine>
        <DivLine>
          <ul>
            {dutiesToTransfer.map((da) => (
              <li style={{ color: isBusyDateForTarget(new Date(da.duty!.dutyDate.date)) ? 'red' : 'black' }}>
                {moment(da.duty!.dutyDate.date).format('ddd DD/MM/YYYY') +
                  ' ' +
                  (da.duty!.dutyType !== undefined ? da.duty!.dutyType!.type : '')}
              </li>
            ))}
          </ul>
        </DivLine>
        <DivLine>
          {dutiesToTransfer.some((da) => isBusyDateForTarget(new Date(da.duty!.dutyDate.date))) && (
            <span style={{ color: 'red' }}>
              <TranslatedMessage id={TLCode.DUTIES_TransferDialog_Blocked_Explained} />
            </span>
          )}
        </DivLine>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            onClick={transferDuties}
            disabled={
              dutiesToTransfer.length === 0 ||
              dutiesToTransfer.some((da) => isBusyDateForTarget(new Date(da.duty!.dutyDate.date)))
            }
          >
            <TranslatedMessage id={TLCode.DUTIES_TransferDialog_TransferBtn} />
          </Button>
          <Button onClick={onCancel}>
            <TranslatedMessage id={TLCode.GENERAL_Cancel} />
          </Button>
        </div>{' '}
      </div>
    </Dialog>
  );
};
